// src/apiFetch.ts
import { getToken, clearAuth, isTokenExpired } from "./auth";

type ApiFetchOptions = RequestInit & {
  onUnauthorized?: () => void;
  /**
   * If true, return the raw Response instead of JSON/text parsing.
   * Useful for file downloads, streaming, etc.
   */
  raw?: boolean;
};

function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

async function safeParseBody(res: Response): Promise<any> {
  // Handle empty responses
  if (res.status === 204) return null;

  const ct = res.headers.get("content-type") || "";
  const text = await res.text();

  if (!text) return null;

  // Prefer JSON when server says JSON
  if (ct.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      // fall through to text if JSON is malformed
      return text;
    }
  }

  // Otherwise return text
  return text;
}

export async function apiFetch(
  input: RequestInfo | URL,
  options: ApiFetchOptions = {},
) {
  const token = getToken();

  // If you call an authenticated endpoint with no token, fail fast
  if (!token) {
    options.onUnauthorized?.();
    throw new Error("Not authenticated");
  }

  // If token is already expired, logout before making request
  if (isTokenExpired(token)) {
    clearAuth();
    options.onUnauthorized?.();
    throw new Error("Session expired");
  }

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${token}`);

  // Don’t force JSON if it’s FormData
  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(input, { ...options, headers });

  if (res.status === 401) {
    clearAuth();
    options.onUnauthorized?.();
    throw new Error("UNAUTHORIZED");
  }

  if (options.raw) return res;

  const body = await safeParseBody(res);

  if (!res.ok) {
    // FastAPI often returns { detail: "..." }
    const msg =
      (body && typeof body === "object" && (body.detail || body.message)) ||
      (typeof body === "string" ? body : null) ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }

  return body;
}
